import { useEffect, useMemo, useState } from "react";
import {
  App,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Spin,
  Typography,
} from "antd";
import {
  createManualIntake,
  getIntakeSchema,
  type IntakeSchemaItem,
} from "../lib/api";
import {
  INTAKE_SOURCES,
  type IntakeSource,
} from "./intakeDetails/types";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (intakeId: string) => void;
}

const SUBMITTER_IDS = new Set(["submitter_name", "submitter_email", "submitter_agency"]);

export default function NewIntakeModal({ open, onClose, onCreated }: Props) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [schema, setSchema] = useState<IntakeSchemaItem[]>([]);
  const [loadingSchema, setLoadingSchema] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || schema.length > 0) return;
    setLoadingSchema(true);
    getIntakeSchema()
      .then(setSchema)
      .catch((error) => {
        if (error instanceof Error) message.error(error.message);
      })
      .finally(() => setLoadingSchema(false));
  }, [open, schema.length, message]);

  const topicSchemaItems = useMemo(
    () => schema.filter((item) => !SUBMITTER_IDS.has(item.id)),
    [schema]
  );

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const answers = topicSchemaItems.map((item) => ({
        questionId: item.id,
        stepTitle: item.stepTitle,
        question: item.question,
        answer: (values[`answer_${item.id}`] || "").trim(),
      }));
      const result = await createManualIntake({
        submitterName: values.submitterName.trim(),
        submitterEmail: values.submitterEmail.trim(),
        submitterAgency: values.submitterAgency.trim(),
        source: values.source as IntakeSource,
        intakeName: (values.intakeName || "").trim() || undefined,
        intakeDescription: (values.intakeDescription || "").trim() || undefined,
        answers,
      });
      message.success(`Intake created: ${result.intakeName}`);
      form.resetFields();
      onCreated(result.intakeId);
    } catch (error) {
      if (error instanceof Error && error.message) message.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title="New intake"
      open={open}
      onCancel={() => {
        if (submitting) return;
        form.resetFields();
        onClose();
      }}
      onOk={handleSubmit}
      okText={submitting ? "Creating…" : "Create intake"}
      okButtonProps={{ loading: submitting }}
      width={760}
      destroyOnClose
    >
      {loadingSchema ? (
        <Space style={{ width: "100%", justifyContent: "center", padding: 32 }}>
          <Spin />
        </Space>
      ) : (
        <Form form={form} layout="vertical">
          <Typography.Title level={5}>Submitter</Typography.Title>
          <Form.Item
            label="Submitter name"
            name="submitterName"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Submitter email"
            name="submitterEmail"
            rules={[
              { required: true, message: "Required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Agency"
            name="submitterAgency"
            rules={[{ required: true, message: "Required" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Source"
            name="source"
            rules={[{ required: true, message: "Pick a source" }]}
          >
            <Select<IntakeSource>
              placeholder="Select source"
              options={INTAKE_SOURCES.map((s) => ({ label: s, value: s }))}
            />
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: 24 }}>
            Naming (optional — AI will generate if blank)
          </Typography.Title>
          <Form.Item label="Intake name" name="intakeName">
            <Input placeholder="e.g., Permit Review Automation" />
          </Form.Item>
          <Form.Item label="Description" name="intakeDescription">
            <Input.TextArea autoSize={{ minRows: 2 }} placeholder="One-sentence summary" />
          </Form.Item>

          <Typography.Title level={5} style={{ marginTop: 24 }}>
            Project intake topics
          </Typography.Title>
          {topicSchemaItems.map((item) => (
            <Form.Item
              key={item.id}
              label={
                <Space direction="vertical" size={0}>
                  <Typography.Text strong>{item.stepTitle}</Typography.Text>
                  <Typography.Text>{item.question}</Typography.Text>
                  {item.description ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {item.description}
                    </Typography.Text>
                  ) : null}
                </Space>
              }
              name={`answer_${item.id}`}
              rules={[{ required: true, message: "Provide an answer" }]}
            >
              <Input.TextArea
                autoSize={{ minRows: 3 }}
                placeholder={item.placeholder || ""}
              />
            </Form.Item>
          ))}
        </Form>
      )}
    </Modal>
  );
}
